/*
 * Legacy Chips component - Copy from PrimeNG 17
 * Created for compatibility since Chips was removed in PrimeNG 18
 */

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
  booleanAttribute,
  forwardRef,
  numberAttribute
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ChipsAddEvent, ChipsClickEvent, ChipsRemoveEvent } from './chips.interface';

export const CHIPS_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => LegacyChipsComponent),
  multi: true
};

let uniqueIdCounter = 0;

/**
 * Legacy Chips component - groups a collection of contents as tags.
 * @group Components
 */
@Component({
  selector: 'p-chips',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      [ngClass]="{
        'p-chips p-component p-input-wrapper': true,
        'p-disabled': disabled,
        'p-focus': focused,
        'p-inputwrapper-filled': (value && value.length) || (inputViewChild?.nativeElement.value && inputViewChild?.nativeElement.value.length),
        'p-inputwrapper-focus': focused
      }"
      [ngStyle]="style"
      [class]="styleClass"
    >
      <ul
        #container
        [ngClass]="{ 'p-inputtext p-chips-multiple-container': true }"
        tabindex="-1"
        role="listbox"
        [attr.aria-labelledby]="ariaLabelledBy"
        [attr.aria-label]="ariaLabel"
        [attr.aria-activedescendant]="focused ? focusedOptionId : undefined"
        [attr.aria-orientation]="'horizontal'"
        (click)="onWrapperClick()"
        (focus)="onContainerFocus()"
        (blur)="onContainerBlur()"
        (keydown)="onContainerKeyDown($event)"
      >
        <li
          #token
          *ngFor="let item of value; let i = index"
          [attr.id]="id + '_chips_item_' + i"
          role="option"
          [attr.ariaLabel]="item"
          [attr.aria-selected]="true"
          [attr.aria-setsize]="value.length"
          [attr.aria-posinset]="i + 1"
          [attr.data-p-focused]="focusedIndex === i"
          [ngClass]="{ 'p-chips-token': true, 'p-focus': focusedIndex === i }"
          (click)="onItemClick($event, item)"
        >
          <span class="p-chips-token-label">{{ field ? resolveFieldData(item, field) : item }}</span>
          <ng-container *ngIf="!disabled">
            <span class="p-chips-token-icon pi pi-times-circle" (click)="removeItem($event, i)" [attr.aria-hidden]="true"></span>
          </ng-container>
        </li>
        <li class="p-chips-input-token" [ngClass]="{ 'p-chips-clearable': showClear && !disabled }" role="option">
          <input
            #inputtext
            type="text"
            [attr.id]="inputId"
            [attr.maxlength]="maxLength"
            [attr.placeholder]="value && value.length ? null : placeholder"
            [attr.tabindex]="tabindex"
            (keydown)="onKeyDown($event)"
            (input)="onInput()"
            (paste)="onPaste($event)"
            (focus)="onInputFocus($event)"
            (blur)="onInputBlur($event)"
            [disabled]="disabled || isMaxedOut"
            [ngStyle]="inputStyle"
            [class]="inputStyleClass"
          />
        </li>
        <li *ngIf="value != null && filled && !disabled && showClear">
          <span class="p-chips-clear-icon pi pi-times" (click)="clear()"></span>
        </li>
      </ul>
    </div>
  `,
  styles: [`
    .p-chips {
      display: inline-flex;
      width: 100%;
    }

    .p-chips-multiple-container {
      margin: 0;
      padding: 0.375rem 0.75rem;
      list-style-type: none;
      cursor: text;
      overflow: hidden;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.25rem;
      box-sizing: border-box;

      /* PrimeNG 18 input styling */
      background: var(--p-inputtext-background, #ffffff);
      border: 1px solid var(--p-inputtext-border-color, #d1d5db);
      border-radius: var(--p-inputtext-border-radius, 6px);
      color: var(--p-inputtext-color, #374151);
      transition: background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s;
    }

    .p-chips.p-focus .p-chips-multiple-container {
      border-color: var(--p-inputtext-focus-border-color, var(--p-primary-color));
      box-shadow: var(--p-inputtext-focus-ring-shadow, 0 0 0 0.2rem rgba(var(--p-primary-500), 0.2));
      outline: 0 none;
    }

    .p-chips.p-disabled .p-chips-multiple-container {
      background: var(--p-inputtext-disabled-background, #f3f4f6);
      border-color: var(--p-inputtext-disabled-border-color, #d1d5db);
      color: var(--p-inputtext-disabled-color, #9ca3af);
      cursor: not-allowed;
    }

    .p-chips-token {
      cursor: default;
      display: inline-flex;
      align-items: center;
      flex: 0 0 auto;
      max-width: 100%;

      /* PrimeNG 18 chip/tag styling */
      background: var(--p-primary-100, #e0e7ff);
      color: var(--p-primary-700, #4338ca);
      border: 1px solid var(--p-primary-300, #a5b4fc);
      border-radius: var(--p-chip-border-radius, 16px);
      padding: 0 0.25rem;
      margin-right: 0.25rem;
      font-size: 0.875rem;
      font-weight: 500;
    }

    .p-chips-token.p-focus {
      background: var(--p-primary-200, #c7d2fe);
      outline: 2px solid var(--p-primary-color);
      outline-offset: 1px;
    }

    .p-chips-token-label {
      min-width: 0%;
      overflow: auto;
      line-height: 1.5;
    }

    .p-chips-token-label::-webkit-scrollbar {
      display: none;
    }

    .p-chips-input-token {
      flex: 1 1 auto;
      display: inline-flex;
      padding: 0.125rem 0;
    }

    .p-chips-token-icon {
      cursor: pointer;
      margin-left: 0.5rem;
      color: var(--p-primary-500, #6366f1);
      font-size: 0.875rem;
      transition: color 0.2s;
    }

    .p-chips-token-icon:hover {
      color: var(--p-primary-700, #4338ca);
    }

    .p-chips-input-token input {
      border: 0 none;
      outline: 0 none;
      background-color: transparent;
      margin: 0;
      padding: 0;
      box-shadow: none;
      border-radius: 0;
      width: 100%;
      font-family: inherit;
      font-size: inherit;
      color: inherit;
    }

    .p-chips-input-token input::placeholder {
      color: var(--p-inputtext-placeholder-color, #9ca3af);
    }

    .p-fluid .p-chips {
      display: flex;
    }

    .p-chips-clear-icon {
      position: absolute;
      top: 50%;
      margin-top: -0.5rem;
      cursor: pointer;
      color: var(--p-inputtext-icon-color, #6b7280);
    }

    .p-chips-clear-icon:hover {
      color: var(--p-inputtext-hover-icon-color, #374151);
    }

    .p-chips-clearable .p-inputtext {
      position: relative;
    }
  `],
  host: {
    class: 'p-element p-inputwrapper irian-custom-cva',
    '[class.p-inputwrapper-filled]': 'filled',
    '[class.p-inputwrapper-focus]': 'focused',
    '[class.p-chips-clearable]': 'showClear'
  },
  providers: [CHIPS_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class LegacyChipsComponent implements ControlValueAccessor {
  /**
   * Inline style of the element.
   * @group Props
   */
  @Input() style: { [klass: string]: any } | null | undefined;
  /**
   * Style class of the element.
   * @group Props
   */
  @Input() styleClass: string | undefined;
  /**
   * When present, it specifies that the element should be disabled.
   * @group Props
   */
  @Input({ transform: booleanAttribute }) disabled: boolean | undefined;
  /**
   * Name of the property to display on a chip.
   * @group Props
   */
  @Input() field: string | undefined;
  /**
   * Advisory information to display on input.
   * @group Props
   */
  @Input() placeholder: string | undefined;
  /**
   * Maximum number of entries allowed.
   * @group Props
   */
  @Input({ transform: numberAttribute }) max: number | undefined;
  /**
   * Maximum length of a chip.
   * @group Props
   */
  @Input() maxLength: number | undefined;
  /**
   * Defines a string that labels the input for accessibility.
   * @group Props
   */
  @Input() ariaLabel: string | undefined;
  /**
   * Establishes relationships between the component and label(s) where its value should be one or more element IDs.
   * @group Props
   */
  @Input() ariaLabelledBy: string | undefined;
  /**
   * Index of the element in tabbing order.
   * @group Props
   */
  @Input({ transform: numberAttribute }) tabindex: number | undefined;
  /**
   * Identifier of the focus input to match a label defined for the component.
   * @group Props
   */
  @Input() inputId: string | undefined;
  /**
   * Whether to allow duplicate values or not.
   * @group Props
   */
  @Input({ transform: booleanAttribute }) allowDuplicate: boolean = true;
  /**
   * Defines whether duplication check should be case-sensitive
   * @group Props
   */
  @Input({ transform: booleanAttribute }) caseSensitiveDuplication: boolean = true;
  /**
   * Inline style of the input field.
   * @group Props
   */
  @Input() inputStyle: { [klass: string]: any } | null | undefined;
  /**
   * Style class of the input field.
   * @group Props
   */
  @Input() inputStyleClass: string | undefined;
  /**
   * Whether to add an item on tab key press.
   * @group Props
   */
  @Input({ transform: booleanAttribute }) addOnTab: boolean | undefined;
  /**
   * Whether to add an item when the input loses focus.
   * @group Props
   */
  @Input({ transform: booleanAttribute }) addOnBlur: boolean | undefined;
  /**
   * Separator char to add an item when pressed in addition to the enter key.
   * @group Props
   */
  @Input() separator: string | RegExp | undefined;
  /**
   * When enabled, a clear icon is displayed to clear the value.
   * @group Props
   */
  @Input({ transform: booleanAttribute }) showClear: boolean = false;
  /**
   * Callback to invoke on chip add.
   * @group Emits
   */
  @Output() onAdd: EventEmitter<ChipsAddEvent> = new EventEmitter<ChipsAddEvent>();
  /**
   * Callback to invoke on chip remove.
   * @group Emits
   */
  @Output() onRemove: EventEmitter<ChipsRemoveEvent> = new EventEmitter<ChipsRemoveEvent>();
  /**
   * Callback to invoke on focus of input field.
   * @group Emits
   */
  @Output() onFocus: EventEmitter<Event> = new EventEmitter<Event>();
  /**
   * Callback to invoke on blur of input field.
   * @group Emits
   */
  @Output() onBlur: EventEmitter<Event> = new EventEmitter<Event>();
  /**
   * Callback to invoke on chip clicked.
   * @group Emits
   */
  @Output() onChipClick: EventEmitter<ChipsClickEvent> = new EventEmitter<ChipsClickEvent>();
  /**
   * Callback to invoke on clear token clicked.
   * @group Emits
   */
  @Output() onClear: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('inputtext') inputViewChild!: ElementRef;

  @ViewChild('container') containerViewChild!: ElementRef;

  value: any;

  onModelChange: Function = () => {};

  onModelTouched: Function = () => {};

  valueChanged: boolean | null = null;

  id = 'legacy_chips_' + uniqueIdCounter++;

  focused: boolean | null = null;

  focusedIndex: number | null = null;

  filled: boolean | null = null;

  get focusedOptionId() {
    return this.focusedIndex !== null ? `${this.id}_chips_item_${this.focusedIndex}` : null;
  }

  get isMaxedOut(): boolean {
    return !!(this.max && this.value && this.max === this.value.length);
  }

  constructor(
    public el: ElementRef,
    public cd: ChangeDetectorRef
  ) {}

  onWrapperClick() {
    this.inputViewChild?.nativeElement.focus();
  }

  onContainerFocus() {
    this.focused = true;
  }

  onContainerBlur() {
    this.focusedIndex = -1;
    this.focused = false;
  }

  onContainerKeyDown(event: KeyboardEvent) {
    switch (event.code) {
      case 'ArrowLeft':
        this.onArrowLeftKeyOn();
        break;
      case 'ArrowRight':
        this.onArrowRightKeyOn();
        break;
      case 'Backspace':
        this.onBackspaceKeyOn(event);
        break;
      case 'Space':
        if (this.focusedIndex !== null && this.value && this.value.length > 0) {
          this.onItemClick(event, this.value[this.focusedIndex]);
        }
        break;
      default:
        break;
    }
  }

  onArrowLeftKeyOn() {
    if (this.inputViewChild.nativeElement.value.length === 0 && this.value && this.value.length > 0) {
      this.focusedIndex = this.focusedIndex === null ? this.value.length - 1 : this.focusedIndex - 1;
      if (this.focusedIndex < 0) this.focusedIndex = 0;
    }
  }

  onArrowRightKeyOn() {
    if (this.inputViewChild.nativeElement.value.length === 0 && this.value && this.value.length > 0) {
      if (this.focusedIndex === this.value.length - 1) {
        this.focusedIndex = null;
        this.inputViewChild?.nativeElement.focus();
      } else {
        this.focusedIndex = (this.focusedIndex || 0) + 1;
      }
    }
  }

  onBackspaceKeyOn(event: Event) {
    if (this.focusedIndex !== null) {
      this.removeItem(event, this.focusedIndex);
    }
  }

  onInput() {
    this.updateFilledState();
  }

  onPaste(event: ClipboardEvent) {
    if (!this.disabled) {
      if (this.separator) {
        const pastedData = event.clipboardData?.getData('text') || '';
        const items = pastedData.split(this.separator);
        items.forEach((item: string) => {
          this.addItem(event, item, true);
        });
        this.inputViewChild.nativeElement.value = '';
        event.preventDefault();
      }
    }
  }

  updateFilledState() {
    if (!this.value || this.value.length === 0) {
      this.filled = this.inputViewChild && this.inputViewChild.nativeElement && this.inputViewChild.nativeElement.value != '';
    } else {
      this.filled = true;
    }
  }

  onItemClick(event: Event, item: any) {
    this.onChipClick.emit({
      originalEvent: event,
      value: item
    });
  }

  writeValue(value: any): void {
    this.value = value;
    this.updateMaxedOut();
    this.updateFilledState();
    this.cd.markForCheck();
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  setDisabledState(val: boolean): void {
    this.disabled = val;
    this.cd.markForCheck();
  }

  resolveFieldData(data: any, field: string): any {
    if (data && field) {
      if (field.indexOf('.') == -1) {
        return data[field];
      } else {
        let fields: string[] = field.split('.');
        let value = data;
        for (var i = 0, len = fields.length; i < len; ++i) {
          value = value[fields[i]];
        }
        return value;
      }
    } else {
      return null;
    }
  }

  onInputFocus(event: FocusEvent) {
    this.focused = true;
    this.focusedIndex = null;
    this.onFocus.emit(event);
  }

  onInputBlur(event: FocusEvent) {
    this.focused = false;
    this.focusedIndex = null;
    if (this.addOnBlur && this.inputViewChild.nativeElement.value) {
      this.addItem(event, this.inputViewChild.nativeElement.value, false);
    }
    this.onModelTouched();
    this.onBlur.emit(event);
  }

  removeItem(event: Event, index: number): void {
    if (this.disabled) {
      return;
    }

    let removedItem = this.value[index];
    this.value = this.value.filter((val: any, i: number) => i != index);
    this.focusedIndex = null;
    this.inputViewChild.nativeElement.focus();

    this.onModelChange(this.value);
    this.onRemove.emit({
      originalEvent: event,
      value: removedItem
    });
    this.updateFilledState();
    this.updateMaxedOut();
  }

  addItem(event: Event, item: string, preventDefault: boolean): void {
    this.value = this.value || [];

    if (item && item.trim().length) {
      const newItemIsDuplicate = this.caseSensitiveDuplication
        ? this.value.includes(item)
        : this.value.some((val: string) => val.toLowerCase() === item.toLowerCase());

      if ((this.allowDuplicate || !newItemIsDuplicate) && !this.isMaxedOut) {
        this.value = [...this.value, item];
        this.onModelChange(this.value);
        this.onAdd.emit({
          originalEvent: event,
          value: item
        });
      }
    }

    this.updateFilledState();
    this.updateMaxedOut();
    this.inputViewChild.nativeElement.value = '';

    if (preventDefault) {
      event.preventDefault();
    }
  }

  public clear() {
    this.value = null;
    this.updateFilledState();
    this.onModelChange(this.value);
    this.updateMaxedOut();
    this.onClear.emit();
  }

  onKeyDown(event: KeyboardEvent) {
    const inputValue = (event.target as HTMLInputElement).value;

    switch (event.code) {
      case 'Backspace':
        if (inputValue.length === 0 && this.value && this.value.length > 0) {
          if (this.focusedIndex !== null) {
            this.removeItem(event, this.focusedIndex);
          } else {
            this.removeItem(event, this.value.length - 1);
          }
        }
        break;

      case 'Enter':
      case 'NumpadEnter':
        if (inputValue && inputValue.trim().length && !this.isMaxedOut) {
          this.addItem(event, inputValue, true);
        }
        break;

      case 'Tab':
        if (this.addOnTab && inputValue && inputValue.trim().length && !this.isMaxedOut) {
          this.addItem(event, inputValue, true);
          event.preventDefault();
        }
        break;

      case 'ArrowLeft':
        if (inputValue.length === 0 && this.value && this.value.length > 0) {
          this.containerViewChild?.nativeElement.focus();
        }
        break;

      case 'ArrowRight':
        event.stopPropagation();
        break;

      default:
        if (this.separator) {
          if (this.separator === event.key || event.key.match(this.separator)) {
            this.addItem(event, inputValue, true);
          }
        }
        break;
    }
  }

  updateMaxedOut(): void {
    if (this.inputViewChild && this.inputViewChild.nativeElement) {
      if (this.isMaxedOut) {
        this.inputViewChild.nativeElement.blur();
        this.inputViewChild.nativeElement.disabled = true;
      } else {
        if (this.disabled) {
          this.inputViewChild.nativeElement.blur();
        }
        this.inputViewChild.nativeElement.disabled = this.disabled || false;
      }
    }
  }
}
